/**
 * Browser-side replacement for `src/main/codeRunner.ts`. JS/TS execution
 * happens inside a Web Worker spawned from a Blob URL (stays in the same
 * origin, no eval-CSP issues). TS is transpiled via `esbuild-wasm` lazily
 * loaded on first use.
 *
 *  - 5s wall-clock timeout via `worker.terminate()`.
 *  - Captures `console.{log,info,debug,warn,error}` → stdout/stderr lines.
 *  - Wraps in `(async () => { ... })()` so top-level `await` works.
 *  - The final return value (REPL-style) is appended to stdout when defined.
 *
 * Not a security sandbox — same caveat as the desktop runner.
 */
import type { CodeRunResult } from '../../shared/ipc';

const TIMEOUT_MS = 5000;

// ----- esbuild-wasm lazy-loader (cached after first call) ---------------

type EsbuildModule = typeof import('esbuild-wasm');
let esbuildPromise: Promise<EsbuildModule> | null = null;
async function getEsbuild(): Promise<EsbuildModule> {
  if (esbuildPromise) return esbuildPromise;
  esbuildPromise = (async () => {
    const mod = await import('esbuild-wasm');
    // Vite bundles the wasm next to the SDK. The default URL works for both
    // dev and build because Vite re-writes the asset reference at compile
    // time. If the SDK ever stops shipping a default `wasmURL` we'd point
    // it explicitly here.
    await mod.initialize({
      wasmURL: new URL('esbuild-wasm/esbuild.wasm', import.meta.url).href,
    });
    return mod;
  })();
  return esbuildPromise;
}

// ----- Worker source: lives as a string we feed to Blob ------------------
//
// Inline rather than a separate file because Vite's worker resolution is
// awkward for inline-only code paths and we don't need bundling — the
// snippet is small and self-contained.

const WORKER_SOURCE = `
self.addEventListener('message', async (event) => {
  const { code } = event.data;
  const stdout = [];
  const stderr = [];

  function formatArg(arg) {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
    if (typeof arg === 'function') return arg.toString();
    if (arg instanceof Error) return arg.name + ': ' + arg.message;
    try { return JSON.stringify(arg, null, 2); } catch { return String(arg); }
  }
  const con = {
    log:   (...a) => stdout.push(a.map(formatArg).join(' ')),
    info:  (...a) => stdout.push(a.map(formatArg).join(' ')),
    debug: (...a) => stdout.push(a.map(formatArg).join(' ')),
    warn:  (...a) => stderr.push(a.map(formatArg).join(' ')),
    error: (...a) => stderr.push(a.map(formatArg).join(' ')),
  };
  // Override the default console so user code's console.* lands in our buffers.
  Object.defineProperty(self, 'console', { value: con, configurable: true });

  try {
    // Wrap so top-level await works and the final expression is captured.
    const fn = new Function('console', 'return (async () => {\\n' + code + '\\n})();');
    const result = await fn(con);
    if (result !== undefined) stdout.push('⇒ ' + formatArg(result));
    self.postMessage({ ok: true, stdout: stdout.join('\\n'), stderr: stderr.join('\\n') });
  } catch (err) {
    self.postMessage({
      ok: false,
      stdout: stdout.join('\\n'),
      stderr: stderr.join('\\n'),
      error: (err && err.message) || String(err),
    });
  }
});
`;

function makeWorker(): Worker {
  const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url);
  // Revoke once the worker has the URL — saves memory on repeat runs.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return worker;
}

export async function runCode(
  code: string,
  language: 'javascript' | 'typescript' = 'javascript',
): Promise<CodeRunResult> {
  const start = performance.now();

  // Transpile TS to JS first if needed.
  let jsSource = code;
  if (language === 'typescript') {
    try {
      const esbuild = await getEsbuild();
      const out = await esbuild.transform(code, {
        loader: 'ts',
        target: 'es2022',
        format: 'esm',
        sourcemap: false,
      });
      jsSource = out.code;
    } catch (err) {
      return {
        stdout: '',
        stderr: '',
        error: `TypeScript compile failed: ${(err as Error).message ?? String(err)}`,
        durationMs: Math.round(performance.now() - start),
      };
    }
  }

  // Run inside a fresh Worker so `console` overrides + a hard timeout work
  // without touching the host page.
  const worker = makeWorker();
  return new Promise<CodeRunResult>((resolve) => {
    const timer = window.setTimeout(() => {
      worker.terminate();
      resolve({
        stdout: '',
        stderr: '',
        error: `Execution timed out after ${TIMEOUT_MS}ms`,
        durationMs: Math.round(performance.now() - start),
      });
    }, TIMEOUT_MS);

    worker.addEventListener('message', (e: MessageEvent) => {
      window.clearTimeout(timer);
      const data = e.data as {
        ok: boolean;
        stdout: string;
        stderr: string;
        error?: string;
      };
      worker.terminate();
      resolve({
        stdout: data.stdout,
        stderr: data.stderr,
        error: data.ok ? undefined : data.error,
        durationMs: Math.round(performance.now() - start),
      });
    });
    worker.addEventListener('error', (e: ErrorEvent) => {
      window.clearTimeout(timer);
      worker.terminate();
      resolve({
        stdout: '',
        stderr: '',
        error: e.message || 'Worker error',
        durationMs: Math.round(performance.now() - start),
      });
    });

    worker.postMessage({ code: jsSource });
  });
}
